/* global URLSearchParams */
/**
 * Build a pre-filled Google Calendar event creation URL.
 * @param {Object} params
 * @param {string} params.title - Event title.
 * @param {string} params.start - Start date from EventDTO (e.g. "20260501T090000Z" or "20260501").
 * @param {string} params.end - End date from EventDTO.
 * @param {string} [params.description] - Event description.
 * @param {string} [params.location] - Event location.
 * @param {string} [params.rrule] - Recurrence rule (e.g. "FREQ=WEEKLY;BYDAY=MO").
 * @returns {string} The Google Calendar URL.
 */
export function buildGoogleCalendarLink({ title, start, end, description, location, rrule }) {
  const base = 'https://calendar.google.com/calendar/render';
  const params = new URLSearchParams({ action: 'TEMPLATE', text: title });

  const startDate = formatGoogleDate(start);
  const endDate = formatGoogleDate(end) || defaultEnd(startDate);
  params.set('dates', `${startDate}/${endDate}`);

  if (description) {
    params.set('details', description);
  }
  if (location) {
    params.set('location', location);
  }
  if (rrule) {
    params.set('recur', `RRULE:${rrule}`);
  }

  return `${base}?${params.toString()}`;
}

/**
 * Convert a date string from EventDTO to Google Calendar format.
 * EventDTO dates are already in compact format: "YYYYMMDDTHHmmssZ" (timed) or "YYYYMMDD" (all-day).
 * Google Calendar expects the same format, so we pass them through directly.
 */
function formatGoogleDate(dateStr) {
  if (!dateStr) {
    return '';
  }
  return dateStr;
}

/**
 * Compute a default end when none is provided.
 * All-day (8-digit): next day. Timed (contains 'T'): +1 hour (approx via string math).
 */
function defaultEnd(startStr) {
  if (!startStr) {
    return '';
  }
  if (startStr.length === 8) {
    // All-day: YYYYMMDD — add 1 day
    const y = parseInt(startStr.slice(0, 4), 10);
    const m = parseInt(startStr.slice(4, 6), 10) - 1;
    const d = parseInt(startStr.slice(6, 8), 10);
    const next = new Date(y, m, d + 1);
    const yy = String(next.getFullYear()).padStart(4, '0');
    const mm = String(next.getMonth() + 1).padStart(2, '0');
    const dd = String(next.getDate()).padStart(2, '0');
    return `${yy}${mm}${dd}`;
  }
  // Timed: YYYYMMDDTHHmmssZ — add 1 hour
  const hh = parseInt(startStr.slice(9, 11), 10);
  const newHh = String(hh + 1).padStart(2, '0');
  return startStr.slice(0, 9) + newHh + startStr.slice(11);
}
